import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { normalizarNombreProveedor } from '@alexiarest/shared';

/**
 * Campo de texto libre que busca mientras escribes y deja crear lo que no
 * existe (Proveedor, Grupo y Presentación en la ficha del ingrediente;
 * revisión de Alex del 12 oct 2026). Reglas para que no se creen duplicados:
 *
 * - Busca sin importar mayúsculas, acentos ni «SA de CV / SL»: «carnes roma»
 *   encuentra «CARNES ROMA SA DE CV»; «verduras», «Frutas y verduras».
 * - Si hay coincidencias, salen primero para elegir.
 * - La última opción es «+ Crear «lo que escribiste»» (salvo que ya exista
 *   igual). NUNCA se crea solo al salir del campo: salir sin elegir deja lo
 *   que había.
 */
export interface OpcionBuscable {
  value: string;
  label: string;
  /** Solo sale si se busca (p. ej. grupos de serie que el restaurante ocultó). */
  soloAlBuscar?: boolean;
}

/** «CARNES ROMA, S.A. de C.V.» → «carnes roma»: sin acentos, signos ni forma jurídica. */
export const comparable = (texto: string): string => normalizarNombreProveedor(texto);

/** Coincidencias de `texto` en `opciones`: las que empiezan igual, primero. */
export function buscarOpciones<T extends OpcionBuscable>(texto: string, opciones: T[]): T[] {
  const aguja = comparable(texto);
  if (!aguja) return opciones.filter((o) => !o.soloAlBuscar);
  const palabras = aguja.split(' ');
  const peso = (o: T): number => {
    const pajar = comparable(o.label);
    if (pajar === aguja) return 0;
    if (pajar.startsWith(aguja)) return 1;
    return 2;
  };
  return opciones
    .filter((o) => { const pajar = comparable(o.label); return palabras.every((p) => pajar.includes(p)); })
    .sort((a, b) => peso(a) - peso(b));
}

/** ¿Ya existe una opción que, comparada sin mayúsculas ni acentos ni «SA de CV», es lo escrito? */
export function existeIgual(texto: string, opciones: OpcionBuscable[]): OpcionBuscable | undefined {
  const aguja = comparable(texto);
  return aguja ? opciones.find((o) => comparable(o.label) === aguja) : undefined;
}

const CREAR = '__crear__';

export function BuscarOCrear({
  value,
  opciones,
  onElegir,
  onCrear,
  ariaLabel,
  placeholder = 'Escribe para buscar…',
  vacio,
  creando = false,
}: {
  /** El value elegido ('' = ninguno). */
  value: string;
  opciones: OpcionBuscable[];
  onElegir: (value: string) => void;
  /** Crear lo escrito. Solo con un clic o Enter sobre «+ Crear «…»». */
  onCrear: (texto: string) => void;
  ariaLabel: string;
  placeholder?: string;
  /** Opción «ninguno» al principio (p. ej. «Sin proveedor»). */
  vacio?: string;
  creando?: boolean;
}): JSX.Element {
  const id = useId();
  const [texto, setTexto] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [resaltado, setResaltado] = useState(0);
  const caja = useRef<HTMLDivElement>(null);

  const elegida = value ? opciones.find((o) => o.value === value) : undefined;
  const etiqueta = elegida?.label ?? (value || vacio || '');
  const limpio = texto.trim();
  const lista = useMemo(() => {
    const encontradas = buscarOpciones(limpio, opciones).slice(0, 50).map((o) => ({ value: o.value, label: o.label }));
    const base = vacio && !limpio ? [{ value: '', label: vacio }, ...encontradas] : encontradas;
    return limpio && !existeIgual(limpio, opciones) ? [...base, { value: CREAR, label: `+ Crear «${limpio}»` }] : base;
  }, [limpio, opciones, vacio]);

  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e: MouseEvent) => {
      if (caja.current && !caja.current.contains(e.target as Node)) { setAbierto(false); setTexto(''); }
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto]);

  const elegir = (v: string) => {
    if (v === CREAR) onCrear(limpio);
    else onElegir(v);
    setTexto('');
    setAbierto(false);
  };

  return (
    <div className="buscador" ref={caja}>
      <input
        id={id}
        className="input buscador__campo"
        type="text"
        autoComplete="off"
        role="combobox"
        aria-expanded={abierto}
        aria-controls={`${id}-lista`}
        aria-label={ariaLabel}
        disabled={creando}
        value={abierto ? texto : creando ? 'Creando…' : etiqueta}
        placeholder={abierto && etiqueta ? etiqueta : placeholder}
        onFocus={() => { setAbierto(true); setTexto(''); setResaltado(0); }}
        onClick={() => setAbierto(true)}
        onBlur={(e) => {
          // Salir del campo NUNCA crea: se cierra y queda lo que había.
          if (!caja.current?.contains(e.relatedTarget as Node)) { setAbierto(false); setTexto(''); }
        }}
        onChange={(e) => { setTexto(e.target.value); setAbierto(true); setResaltado(0); }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            setAbierto(true);
            const paso = e.key === 'ArrowDown' ? 1 : -1;
            setResaltado((i) => (lista.length ? (i + paso + lista.length) % lista.length : 0));
          } else if (e.key === 'Enter' && abierto) {
            e.preventDefault();
            const opcion = lista[Math.min(resaltado, lista.length - 1)];
            if (opcion) elegir(opcion.value);
          } else if (e.key === 'Escape' && abierto) {
            e.stopPropagation();
            setAbierto(false);
            setTexto('');
          }
        }}
      />
      {abierto && (
        <ul className="buscador__lista" id={`${id}-lista`} role="listbox">
          {lista.length === 0 && <li className="buscador__vacio">Escribe para buscar o crear</li>}
          {lista.map((o, i) => (
            <li key={o.value || '__vacio__'}>
              <button
                type="button"
                role="option"
                tabIndex={-1}
                aria-selected={o.value === value}
                className={`buscador__opcion${i === resaltado ? ' is-resaltada' : ''}${o.value === CREAR ? ' buscador__opcion--crear' : ''}`}
                onMouseEnter={() => setResaltado(i)}
                onClick={() => elegir(o.value)}
              >
                {o.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
